import { api, ApiError } from './api';
import type {
  Product,
  Category,
  MasterCard,
  Printing,
  TcgCardSuggestion,
  TcgListing,
} from '@/types';

const YUGIOH_SOURCE_API_BASE_URL = (
  import.meta.env.VITE_YUGIOH_SOURCE_API_URL ||
  (import.meta.env.DEV ? '/yugioh-source/api/v7' : 'https://db.ygoprodeck.com/api/v7')
).replace(/\/$/, '');
const TCG_SET_CODE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)+$/i;

type ExternalYugiohCardSet = {
  set_name?: string;
  set_code?: string;
  set_rarity?: string;
  set_rarity_code?: string;
  set_edition?: string;
  set_price?: string;
  set_price_low?: string;
  set_url?: string;
};

type ExternalYugiohCardImage = {
  id?: number;
  image_url?: string;
  image_url_small?: string;
  image_url_cropped?: string;
};

type ExternalYugiohCard = {
  id: number;
  name: string;
  type: string;
  desc: string;
  race?: string;
  archetype?: string;
  attribute?: string;
  level?: number;
  rank?: number;
  atk?: number;
  def?: number;
  scale?: number;
  linkval?: number;
  card_sets?: ExternalYugiohCardSet[];
  card_images?: ExternalYugiohCardImage[];
};

type ExternalYugiohResponse = {
  data?: ExternalYugiohCard[];
  error?: string;
};

export function isLikelyTcgSetCode(value: string): boolean {
  const trimmed = value.trim();
  return TCG_SET_CODE_PATTERN.test(trimmed) && /\d/.test(trimmed);
}

export function normalizeTcgSetCode(value: string): string {
  return value.trim().toUpperCase();
}

export interface GetProductsParams {
  skip?: number;
  take?: number;
  currency?: string;
  q?: string;
}

export interface GetPrintingsParams {
  skip?: number;
  take?: number;
  setCode?: string;
  rarityCode?: string;
  edition?: string;
  currency?: string;
  availableOnly?: boolean;
}

export interface SearchPrintingsParams extends GetPrintingsParams {
  q: string;
}

export interface SearchTcgCardSuggestionsParams {
  q: string;
  take?: number;
  currency?: string;
}

export interface GetPrintingsBySetCodesParams {
  currency?: string;
  takePerCode?: number;
  availableOnly?: boolean;
}

export interface GetPrintingByIdParams {
  cardId: string;
  setCode: string;
  setRarityCode?: string | null;
  setEdition: string;
  currency?: string;
}

export interface GetExternalPrintingByIdParams {
  cardId: string;
  setCode: string;
  setRarityCode?: string | null;
  setEdition?: string | null;
  currency?: string;
}

// Deprecated - use GetPrintingsParams instead
export interface GetMasterCardsParams {
  skip?: number;
  take?: number;
}

// Deprecated - use SearchPrintingsParams instead
export interface SearchMasterCardsParams extends GetMasterCardsParams {
  q: string;
}

function normalizeSourceKey(value?: string | number | null): string {
  return String(value ?? '').trim().toLowerCase();
}

function nullableText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function nullableNumber(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function buildYugiohSourceUrl(params: URLSearchParams): string {
  const query = params.toString();
  return `${YUGIOH_SOURCE_API_BASE_URL}/cardinfo.php${query ? `?${query}` : ''}`;
}

async function fetchYugiohSourceCards(params: URLSearchParams): Promise<ExternalYugiohCard[]> {
  const response = await fetch(buildYugiohSourceUrl(params), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    return [];
  }

  const payload = (await response.json()) as ExternalYugiohResponse;
  return Array.isArray(payload.data) ? payload.data : [];
}

function mapExternalSetToVariant(
  cardId: number,
  set: ExternalYugiohCardSet,
  index: number,
) {
  const setCode = nullableText(set.set_code);
  const setRarity = nullableText(set.set_rarity);
  const setRarityCode = nullableText(set.set_rarity_code);
  const setEdition = nullableText(set.set_edition);
  const setPrice = nullableNumber(set.set_price);
  const setPriceLow = nullableNumber(set.set_price_low);

  return {
    id: `${cardId}-${setCode ?? 'set'}-${setRarityCode ?? setRarity ?? index}-${setEdition ?? 'default'}`,
    cardId,
    setName: nullableText(set.set_name),
    setCode,
    setRarity,
    setRarityCode,
    setPrice,
    setPriceCurrency: setPrice != null ? 'USD' : undefined,
    setPriceUsd: setPrice,
    setPriceLow,
    setPriceLowCurrency: setPriceLow != null ? 'USD' : undefined,
    setPriceLowUsd: setPriceLow,
    setEdition,
    setUrl: nullableText(set.set_url),
  };
}

function mapExternalCardToSuggestion(card: ExternalYugiohCard): TcgCardSuggestion {
  return {
    card: {
      id: card.id,
      name: card.name,
      type: card.type,
      description: card.desc,
      race: card.race ?? null,
      archetype: card.archetype ?? null,
      attribute: card.attribute ?? null,
      level: card.level ?? null,
      rank: card.rank ?? null,
      atk: card.atk ?? null,
      def: card.def ?? null,
      scale: card.scale ?? null,
      linkval: card.linkval ?? null,
    },
    variants: (card.card_sets ?? []).map((set, index) =>
      mapExternalSetToVariant(card.id, set, index),
    ),
    images: (card.card_images ?? []).map((image) => ({
      id: image.id,
      cardId: card.id,
      imageUrl: image.image_url ?? null,
      imageUrlSmall: image.image_url_small ?? null,
      imageUrlCropped: image.image_url_cropped ?? null,
    })),
  };
}

function selectExternalSet(
  card: ExternalYugiohCard,
  params: GetExternalPrintingByIdParams,
): ExternalYugiohCardSet | null {
  const setCode = normalizeSourceKey(params.setCode);
  const rarity = normalizeSourceKey(params.setRarityCode);
  const edition = normalizeSourceKey(params.setEdition);
  const sets = card.card_sets ?? [];

  return (
    sets.find((set) => {
      const codeMatches = normalizeSourceKey(set.set_code) === setCode;
      if (!codeMatches) return false;

      const rarityMatches =
        !rarity ||
        normalizeSourceKey(set.set_rarity_code) === rarity ||
        normalizeSourceKey(set.set_rarity) === rarity;
      const editionMatches = !edition || normalizeSourceKey(set.set_edition) === edition;
      return rarityMatches && editionMatches;
    }) ??
    sets.find((set) => normalizeSourceKey(set.set_code) === setCode) ??
    null
  );
}

function mapExternalCardToPrinting(
  card: ExternalYugiohCard,
  set: ExternalYugiohCardSet,
): Printing {
  const variant = mapExternalSetToVariant(card.id, set, 0);

  return {
    id: String(variant.id),
    setCode: variant.setCode,
    setName: variant.setName,
    setRarity: variant.setRarity,
    setRarityCode: variant.setRarityCode,
    setEdition: variant.setEdition,
    setPrice: variant.setPrice,
    setPriceCurrency: variant.setPriceCurrency,
    setPriceUsd: variant.setPriceUsd,
    setPriceLow: variant.setPriceLow,
    setPriceLowCurrency: variant.setPriceLowCurrency,
    setPriceLowUsd: variant.setPriceLowUsd,
    setUrl: variant.setUrl,
    card: {
      id: String(card.id),
      name: card.name,
      type: card.type,
      description: card.desc,
      race: card.race ?? null,
      archetype: card.archetype ?? null,
      attribute: card.attribute ?? null,
      level: card.level ?? null,
      rank: card.rank ?? null,
      atk: card.atk ?? null,
      def: card.def ?? null,
      scale: card.scale ?? null,
      linkval: card.linkval ?? null,
      images: (card.card_images ?? []).map((image) => ({
        id: image.id,
        cardId: card.id,
        imageUrl: image.image_url ?? null,
        imageUrlSmall: image.image_url_small ?? null,
        imageUrlCropped: image.image_url_cropped ?? null,
      })),
    },
    availability: {
      isAvailable: false,
    },
    aggregatedStock: {
      aggregatedStock: 0,
      listingCount: 0,
    },
    fromPrice: null,
    listings: [],
  };
}

class CatalogService {
  // Normal Products
  async getActiveProducts(params?: GetProductsParams): Promise<Product[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
      if (params?.take !== undefined) queryParams.append('take', params.take.toString());
      if (params?.currency) queryParams.append('currency', params.currency);
      if (params?.q?.trim()) queryParams.append('q', params.q.trim());

      const query = queryParams.toString();
      return await api.get<Product[]>(`/products${query ? `?${query}` : ''}`);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new Error('Failed to fetch products');
    }
  }

  async getProductById(id: string, params?: { currency?: string }): Promise<Product> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.currency) queryParams.append('currency', params.currency);
      const query = queryParams.toString();
      return await api.get<Product>(`/products/${id}${query ? `?${query}` : ''}`);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 404) {
          throw new Error('Product not found');
        }
        throw error;
      }
      throw new Error('Failed to fetch product');
    }
  }

  async getProductBySlug(slug: string): Promise<Product> {
    try {
      return await api.get<Product>(`/products/slug/${slug}`);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 404) {
          throw new Error('Product not found');
        }
        throw error;
      }
      throw new Error('Failed to fetch product');
    }
  }

  async getProductsByCategory(categoryId: string, currency?: string): Promise<Product[]> {
    try {
      const query = currency ? `?currency=${encodeURIComponent(currency)}` : '';
      return await api.get<Product[]>(`/categories/${categoryId}/products${query}`);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 404) {
          throw new Error('Category not found');
        }
        throw error;
      }
      throw new Error('Failed to fetch products');
    }
  }

  async getVisibleCategories(): Promise<Category[]> {
    try {
      return await api.get<Category[]>('/categories');
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new Error('Failed to fetch categories');
    }
  }

  // TCG Printings (CardSet-based API)
  async getPrintings(params?: GetPrintingsParams): Promise<Printing[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
      if (params?.take !== undefined) queryParams.append('take', params.take.toString());
      if (params?.setCode) queryParams.append('setCode', params.setCode);
      if (params?.rarityCode) queryParams.append('rarityCode', params.rarityCode);
      if (params?.edition) queryParams.append('edition', params.edition);
      if (params?.currency) queryParams.append('currency', params.currency);
      if (params?.availableOnly) queryParams.append('availableOnly', 'true');
      
      const query = queryParams.toString();
      return await api.get<Printing[]>(`/tcg/cards${query ? `?${query}` : ''}`);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new Error('Failed to fetch TCG printings');
    }
  }

  async searchPrintings(params: SearchPrintingsParams): Promise<Printing[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('q', params.q);
      if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
      if (params.take !== undefined) queryParams.append('take', params.take.toString());
      if (params.setCode) queryParams.append('setCode', params.setCode);
      if (params.rarityCode) queryParams.append('rarityCode', params.rarityCode);
      if (params.edition) queryParams.append('edition', params.edition);
      if (params.currency) queryParams.append('currency', params.currency);
      if (params.availableOnly) queryParams.append('availableOnly', 'true');
      
      return await api.get<Printing[]>(`/tcg/cards/search?${queryParams.toString()}`);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new Error('Failed to search TCG printings');
    }
  }

  async searchTcgCardSuggestions(
    params: SearchTcgCardSuggestionsParams,
  ): Promise<TcgCardSuggestion[]> {
    const q = params.q.trim();
    const isSetCodeSearch = isLikelyTcgSetCode(q);
    if (!isSetCodeSearch && q.length < 3) {
      return [];
    }

    const localQueryParams = new URLSearchParams();
    if (isSetCodeSearch) {
      localQueryParams.append('setCode', normalizeTcgSetCode(q));
    } else {
      localQueryParams.append('q', q);
    }
    if (params.take !== undefined) {
      localQueryParams.append('take', Math.min(params.take, 10).toString());
    }
    if (params.currency) {
      localQueryParams.append('currency', params.currency);
    }

    const searchLocal = async () =>
      api.get<TcgCardSuggestion[]>(`/tcg/master-cards/search?${localQueryParams.toString()}`);

    try {
      if (isSetCodeSearch) {
        return await searchLocal();
      }

      try {
        const sourceParams = new URLSearchParams();
        sourceParams.append('fname', q);

        const externalCards = await fetchYugiohSourceCards(sourceParams);
        const externalSuggestions = externalCards
          .map(mapExternalCardToSuggestion)
          .filter((suggestion) => suggestion.variants.length > 0)
          .slice(0, Math.min(params.take ?? 10, 10));

        if (externalSuggestions.length > 0) {
          return externalSuggestions;
        }
      } catch {
        // Keep the search useful even if the public catalog source is unavailable.
      }

      return await searchLocal();
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 404) {
          return [];
        }
        throw error;
      }
      throw new Error('Failed to search TCG card suggestions');
    }
  }

  async getExternalPrintingById(params: GetExternalPrintingByIdParams): Promise<Printing> {
    try {
      const sourceParams = new URLSearchParams();
      sourceParams.append('id', params.cardId);

      const [card] = await fetchYugiohSourceCards(sourceParams);
      if (!card) {
        throw new Error('Printing not found');
      }

      const set = selectExternalSet(card, params);
      if (!set) {
        throw new Error('Printing not found');
      }

      return mapExternalCardToPrinting(card, set);
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to fetch external TCG printing');
    }
  }

  async getPrintingsBySetCodes(
    setCodes: string[],
    params?: GetPrintingsBySetCodesParams,
  ): Promise<Printing[]> {
    const codes = Array.from(
      new Set(
        setCodes
          .map((setCode) => normalizeTcgSetCode(setCode))
          .filter((setCode) => setCode.length > 0),
      ),
    );

    if (codes.length === 0) {
      return [];
    }

    const results = await Promise.all(
      codes.map((setCode) =>
        this.getPrintings({
          setCode,
          take: params?.takePerCode ?? 20,
          currency: params?.currency,
          availableOnly: params?.availableOnly,
        }),
      ),
    );

    const seen = new Set<string>();
    return results.flat().filter((printing) => {
      const key = [
        printing.card.id,
        printing.setCode ?? '',
        printing.setRarityCode ?? printing.setRarity ?? '',
        printing.setEdition ?? '',
        printing.id,
      ].join('|');
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  async getPrintingById(params: GetPrintingByIdParams): Promise<Printing> {
    try {
      const queryParams = new URLSearchParams({
        setCode: params.setCode,
        setEdition: params.setEdition,
      });
      
      // Only include setRarityCode if it's provided and not empty
      if (params.setRarityCode && params.setRarityCode.trim() !== '') {
        queryParams.append('setRarityCode', params.setRarityCode);
      }
      
      if (params.currency) {
        queryParams.append('currency', params.currency);
      }
      
      return await api.get<Printing>(`/tcg/cards/${params.cardId}?${queryParams.toString()}`);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 404) {
          throw new Error('Printing not found');
        }
        if (error.status === 400) {
          throw new Error('Missing required printing identifier parameters');
        }
        throw error;
      }
      throw new Error('Failed to fetch TCG printing');
    }
  }

  // Deprecated methods - kept for backwards compatibility, but should use printing-based methods
  async getMasterCards(params?: GetMasterCardsParams): Promise<MasterCard[]> {
    // Legacy method - delegate to getPrintings and transform
    // Note: This is a simplified transformation - may not preserve all fields
    const printings = await this.getPrintings(params);
    return printings.map((p) => ({
      id: p.card.id,
      name: p.card.name,
      description: p.card.description,
      imageUrl: null, // Printing doesn't include imageUrl directly
      cardNumber: null,
      rarity: p.setRarity,
      attribute: p.card.attribute,
      level: p.card.level,
      attack: p.card.atk,
      defense: p.card.def,
      cardType: p.card.type,
      createdAt: '',
      updatedAt: '',
      availability: p.availability.isAvailable ? 'IN_STOCK' : 'OUT_OF_STOCK',
      aggregatedStock: p.aggregatedStock.aggregatedStock,
      fromPrice: p.fromPrice,
    }));
  }

  async searchMasterCards(params: SearchMasterCardsParams): Promise<MasterCard[]> {
    // Legacy method - delegate to searchPrintings and transform
    const printings = await this.searchPrintings(params);
    return printings.map((p) => ({
      id: p.card.id,
      name: p.card.name,
      description: p.card.description,
      imageUrl: null,
      cardNumber: null,
      rarity: p.setRarity,
      attribute: p.card.attribute,
      level: p.card.level,
      attack: p.card.atk,
      defense: p.card.def,
      cardType: p.card.type,
      createdAt: '',
      updatedAt: '',
      availability: p.availability.isAvailable ? 'IN_STOCK' : 'OUT_OF_STOCK',
      aggregatedStock: p.aggregatedStock.aggregatedStock,
      fromPrice: p.fromPrice,
    }));
  }

  async getMasterCardById(_id: string): Promise<MasterCard> {
    // Legacy method - this will fail without printing identifier
    // Throw error to encourage migration to getPrintingById
    throw new Error('getMasterCardById is deprecated. Use getPrintingById with printing identifier (setCode, setRarityCode, setEdition) instead.');
  }

  // TCG Listings (used for separate endpoint call, but listings are usually included in card detail)
  async getActiveListingsByMasterCard(masterCardId: string): Promise<TcgListing[]> {
    try {
      const listings = await api.get<TcgListing[]>(`/tcg/cards/${masterCardId}/listings`);
      
      return listings.map(listing => ({
        ...listing,
        sellerLabel: listing.seller?.role === 'ADMINISTRATOR' ? 'ADMIN' : 'SELLER',
      }));
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 404) {
          throw new Error('Master card not found');
        }
        throw error;
      }
      throw new Error('Failed to fetch TCG listings');
    }
  }

  /** Get all listings for a seller (authenticated, seller can only request own). */
  async getSellerListings(sellerId: string, includeArchived = false): Promise<TcgListing[]> {
    try {
      const q = includeArchived ? '?includeArchived=true' : '';
      return await api.get<TcgListing[]>(`/tcg/sellers/${sellerId}/listings${q}`);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 403) {
          throw new Error('Access denied');
        }
        if (error.status === 404) {
          throw new Error('Seller not found');
        }
        throw error;
      }
      throw new Error('Failed to fetch seller listings');
    }
  }
}

export const catalogService = new CatalogService();
